import { bucketLog } from '@bucketplace/bucket-log';
import {
  Flex,
  FONT_STYLE,
  MEDIA_QUERY,
  Text,
} from '@bucketplace/design-system';
import { sendDeepLinkAction } from '@bucketplace/owap-sdk';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import gsap from 'gsap';
import Link from 'next/link';
import {
  forwardRef,
  MouseEvent,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { LIFE_EVENT_TEXT } from '@/brand/common/style';
import { AnimationComponentRef } from '@/brand/hooks';
import { useOwapInfo } from '@/hooks';

import { COMMUNITY_LINK, EVENT_SECTION_IMAGE } from '../constants';
import { Container } from '../RebrandSectionContainer';
import { getRebrandStyle } from '../utils';

export const EventSection = forwardRef<AnimationComponentRef>((_, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { isOwap } = useOwapInfo();

  const config = useMemo(
    () => ({
      steps: [
        {
          target: containerRef,
          from: { opacity: 0, y: 50 },
          to: { opacity: 1, y: 0, duration: 0.07 },
          position: '-=2',
        },
        {
          target: containerRef,
          to: {
            duration: 0.5,
          },
          position: '>',
        },
      ],
      options: {
        trigger: containerRef,
        start: 'top top',
        end: '+=200%',
        scrub: 1,
        invalidateOnRefresh: true,
        refreshPriority: 1,
        pin: false,
        anticipatePin: 0,
        normalizeScroll: true,
      },
    }),
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      createTimeline() {
        const tl = gsap.timeline({
          defaults: {
            ease: 'none',
          },
        });
        for (const s of config.steps) {
          const el = s.target?.current;
          if (!el) {
            continue;
          }
          if (s.from) {
            tl.fromTo(el, s.from, s.to, s.position ?? '>');
          } else {
            tl.to(el, s.to, s.position ?? '>');
          }
        }
        return tl;
      },
    }),
    [config.steps],
  );

  const handleCommunityLinkClick = (event: MouseEvent): void => {
    bucketLog({
      category: 'click',
      type: 'button',
      section_id: 'EVENT',
      id: 'PARTICIPATE',
    });

    if (!isOwap) {
      return;
    }

    event.preventDefault();
    sendDeepLinkAction('move', {
      type: 'page',
      url: COMMUNITY_LINK,
      isNative: true,
      isAllowOutboundLink: false,
    });
  };

  return (
    <Container
      ref={containerRef}
      p={{
        mobile: '0 16px',
        laptop: '0 60px',
      }}
    >
      <ImageWrapper>
        <StyledImage
          src={EVENT_SECTION_IMAGE}
          alt="이벤트 이미지"
          loading="lazy"
          decoding="async"
          crossOrigin="anonymous"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </ImageWrapper>
      <Flex
        position="absolute"
        w="100%"
        h="100%"
        align="center"
        justify="center"
        gap={{
          mobile: 20,
          laptop: 28,
        }}
      >
        <StyledText
          as="p"
          color="foregroundInverse"
          whiteSpace="pre-line"
          align="center"
          weight={700}
        >
          {'오늘의집과 함께한\n삶의 변화를 들려주세요'}
          <Text
            as="p"
            variant={{
              mobile: 'body14L20',
              laptop: 'body16L24',
            }}
            weight={500}
            css={{
              color: LIFE_EVENT_TEXT,
              marginTop: 8,
            }}
          >
            참여 기간 : 2025.10.14 - 2025.10.27
          </Text>
        </StyledText>
        <Link href={COMMUNITY_LINK}>
          <StyledButton type="button" onClick={handleCommunityLinkClick}>
            이벤트 참여하기
          </StyledButton>
        </Link>
      </Flex>
    </Container>
  );
});

const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  border-radius: 24px;
  overflow: hidden;
  width: 100%;
  height: 520px;
  max-width: ${getRebrandStyle('eventSectionImage', 'width', 'laptop')}px;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.7) 100%
    );
  }
`;

const StyledImage = styled.img`
  object-fit: cover;
  object-position: center;
  width: 100%;
  height: 100%;
`;

const StyledText = styled(Text)`
  font-size: 24px;
  line-height: 32px;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    font-size: 42px;
    line-height: 54.6px;
  `,
  )}
`;

const StyledButton = styled.button`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: center;
  align-items: center;

  width: fit-content;
  color: ${({ theme }) => theme.colors.foregroundInverse};
  ${FONT_STYLE.body16L24}
  font-weight: 700;

  background-color: #FFFFFF38;
  backdrop-filter: blur(35px);
  padding: 16px 24px;
  border-radius: 500px;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    padding: 24px 36px;
    ${FONT_STYLE.heading18}
  `,
  )}
`;

EventSection.displayName = 'EventSection';
